import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { formatMoney, invoiceStatusLabel } from '../../lib/hostInvoice';
import { accountingCsv, downloadCsv, type AccountingRow } from '../../lib/invoiceExports';
import { Button, Spinner } from '../../components/design-system';
import { InvoiceReviewModal } from '../../components/admin/InvoiceReviewModal';
import { PayoutRunPanel } from '../../components/admin/PayoutRunPanel';
import type { AdminHostInvoice, HostInvoiceStatus } from '../../types';

type Tab = 'review' | 'pay' | 'paid' | 'rejected';

const TABS: { id: Tab; label: string; status: HostInvoiceStatus; empty: string }[] = [
  { id: 'review', label: 'Needs review', status: 'submitted', empty: 'No invoices are waiting for review.' },
  { id: 'pay', label: 'Ready to pay', status: 'approved', empty: 'No approved invoices are waiting for payment.' },
  { id: 'paid', label: 'Paid', status: 'paid', empty: 'No invoices have been paid yet.' },
  { id: 'rejected', label: 'Sent back', status: 'rejected', empty: 'No invoices have been sent back.' },
];

const day = (iso: string) => new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });

function currentMonth(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 7);
}

export function AdminInvoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<AdminHostInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState<Tab>('review');
  const [open, setOpen] = useState<AdminHostInvoice | null>(null);
  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set());
  const [month, setMonth] = useState(currentMonth());
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('host_invoices')
      .select('*, profiles!host_invoices_host_id_fkey(full_name), host_invoice_items(count)')
      .order('submitted_at', { ascending: false });
    if (error) { setLoadError(true); setLoading(false); return; }
    setLoadError(false);
    setInvoices((data as unknown as AdminHostInvoice[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const byTab = useMemo(() => {
    const out: Record<Tab, AdminHostInvoice[]> = { review: [], pay: [], paid: [], rejected: [] };
    for (const inv of invoices) {
      const t = TABS.find((x) => x.status === inv.status);
      if (t) out[t.id].push(inv);
    }
    return out;
  }, [invoices]);

  const rows = byTab[tab];
  const picked = byTab.pay.filter((i) => pickedIds.has(i.id));

  function toggle(id: string) {
    setPickedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  function toggleAll() {
    setPickedIds(picked.length === byTab.pay.length ? new Set() : new Set(byTab.pay.map((i) => i.id)));
  }

  async function exportAccounting() {
    setExporting(true);
    const inMonth = byTab.paid.filter((i) => i.paid_at && i.paid_at.slice(0, 7) === month);
    const hostIds = [...new Set(inMonth.map((i) => i.host_id))];
    const { data } = hostIds.length
      ? await supabase.from('host_billing_details').select('host_id, nif').in('host_id', hostIds)
      : { data: [] };
    const nifByHost = new Map(((data ?? []) as { host_id: string; nif: string }[]).map((b) => [b.host_id, b.nif]));
    const csvRows: AccountingRow[] = inMonth.map((i) => ({
      number: i.number,
      host_name: i.profiles?.full_name ?? null,
      host_nif: nifByHost.get(i.host_id) ?? null,
      invoice_kind: i.invoice_kind,
      invoice_number: i.invoice_number,
      invoice_date: i.invoice_date,
      invoice_base_cents: i.invoice_base_cents,
      invoice_vat_cents: i.invoice_vat_cents,
      invoice_vat_exemption: i.invoice_vat_exemption,
      total_cents: i.total_cents,
      invoice_withholding_cents: i.invoice_withholding_cents,
      invoice_payable_cents: i.invoice_payable_cents,
      currency: i.currency,
      paid_at: i.paid_at,
      payment_reference: i.payment_reference,
      trips: i.host_invoice_items?.[0]?.count ?? 0,
    }));
    downloadCsv(`sekretly-paid-invoices-${month}.csv`, accountingCsv(csvRows));
    setExporting(false);
  }

  if (loading) {
    return <div className="min-h-dvh bg-app-bg flex items-center justify-center"><Spinner /></div>;
  }

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Host invoices</h1>
        <p className="text-app-muted text-sm mb-6">Review what hosts send, approve it, and pay approved invoices in a payout run.</p>

        {loadError ? <p role="alert" className="text-sm text-crimson mb-4">We couldn’t load the invoices. Please refresh.</p> : null}

        <div role="tablist" aria-label="Invoice status" className="flex gap-1.5 mb-5 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`shrink-0 rounded-pill px-3.5 py-1.5 text-[12.5px] font-medium transition-colors ${tab === t.id ? 'bg-app-fg text-app-bg' : 'bg-app-card text-app-fg-2 hover:text-app-fg'}`}
            >
              {t.label} <span className="opacity-70">{byTab[t.id].length}</span>
            </button>
          ))}
        </div>

        {tab === 'pay' && picked.length > 0 && user && (
          <PayoutRunPanel
            invoices={picked}
            adminId={user.id}
            onClear={() => setPickedIds(new Set())}
            onDone={() => { setPickedIds(new Set()); void load(); }}
          />
        )}

        {tab === 'pay' && byTab.pay.length > 1 && (
          <div className="mb-2">
            <button type="button" onClick={toggleAll} className="text-xs text-gold underline underline-offset-2">
              {picked.length === byTab.pay.length ? 'Clear selection' : `Select all ${byTab.pay.length}`}
            </button>
          </div>
        )}

        {tab === 'paid' && (
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="export-month" className="text-xs text-app-muted">Month</label>
              <input id="export-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                className="bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            </div>
            <Button variant="secondary" size="sm" onClick={() => void exportAccounting()} disabled={exporting}>
              {exporting ? 'Preparing…' : 'Export for accounting (CSV)'}
            </Button>
          </div>
        )}

        {rows.length === 0 ? (
          <p className="text-sm text-app-muted py-10 text-center">{TABS.find((t) => t.id === tab)!.empty}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {rows.map((inv) => {
              const status = invoiceStatusLabel(inv);
              const trips = inv.host_invoice_items?.[0]?.count ?? 0;
              return (
                <div key={inv.id} className="bg-app-card rounded-xl border border-app-border p-4 flex items-center gap-3">
                  {tab === 'pay' && (
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-gold shrink-0"
                      checked={pickedIds.has(inv.id)}
                      onChange={() => toggle(inv.id)}
                      aria-label={`Include invoice ${inv.invoice_number} from ${inv.profiles?.full_name ?? 'host'} in the payout run`}
                    />
                  )}
                  <button type="button" onClick={() => setOpen(inv)} className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-app-fg truncate">
                      {inv.profiles?.full_name ?? 'Host'} · {inv.invoice_number}
                      {inv.host_id === user?.id && <span className="ml-2 text-[11px] text-gold">yours</span>}
                    </p>
                    <p className="text-xs text-app-muted">{inv.number} · {trips} trip{trips === 1 ? '' : 's'} · sent {day(inv.submitted_at)}</p>
                    <p className={`text-xs mt-0.5 ${status.tone === 'bad' ? 'text-crimson' : status.tone === 'good' ? 'text-emerald-500' : 'text-app-muted'}`}>{status.label}</p>
                  </button>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-app-fg">{formatMoney(inv.invoice_payable_cents, inv.currency)}</p>
                    {inv.invoice_payable_cents !== inv.total_cents && (
                      <p className="text-[11px] text-app-muted">of {formatMoney(inv.total_cents, inv.currency)}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && user && (
        <InvoiceReviewModal
          invoice={open}
          adminId={user.id}
          onClose={() => setOpen(null)}
          onChanged={() => { setOpen(null); void load(); }}
        />
      )}
    </div>
  );
}

export default AdminInvoices;
